import { Check, Copy, Download } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import type { Challenge } from "@/lib/api";

/**
 * Google Drive id of the shared assets archive (textures, panel models, URDFs).
 *
 * Unlike the maze, this is the same for every challenge and changes only when
 * the asset pack itself is rebuilt, so the API does not carry it per-challenge.
 * It is an env var rather than a literal so a TA can update it without touching
 * code. Default matches vis_nav_core.py's ESSENTIAL_FILE_ID.
 */
const ESSENTIAL_FILE_ID: string =
  import.meta.env.VITE_ESSENTIAL_FILE_ID || "1CvIOxnKO8Z8NDBh-kKOpUuWEgPinLLSV";

function buildStartupJson(challenge: Challenge): string {
  return `${JSON.stringify(
    {
      ESSENTIAL_FILE_ID,
      MAZE_FILE_ID: challenge.maze_id,
      MAZE_FILE_MD5_KEY: challenge.maze_md5,
    },
    null,
    4,
  )}\n`;
}

/**
 * The startup.json a student must place in their vis_nav_player checkout.
 *
 * This is the step most likely to go wrong. The game reads ./startup.json
 * relative to the process working directory and MD5-checks the maze it
 * downloads; a stale file means the student records against the wrong maze and
 * every submission is rejected as "Maze mismatch" — the single most common
 * failure in the archived term.
 */
export function StartupJsonCard({ challenge }: { challenge: Challenge }) {
  const [copied, setCopied] = useState(false);
  const content = buildStartupJson(challenge);

  async function copy() {
    try {
      await navigator.clipboard.writeText(content);
      setCopied(true);
      toast.success("startup.json copied");
      setTimeout(() => setCopied(false), 2000);
    } catch {
      toast.error("Could not copy — select the text manually");
    }
  }

  function download() {
    const blob = new Blob([content], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = "startup.json";
    link.click();
    URL.revokeObjectURL(url);
  }

  // A plain block, not a Card: it sits inside the page's "Get started" card, and a
  // card within a card reads as two unrelated things.
  return (
    <div className="min-w-0 space-y-2">
      <div className="flex flex-wrap items-baseline justify-between gap-2">
        <p className="text-sm font-medium">startup.json</p>
        <p className="text-muted-foreground text-xs">
          Replace this file in your{" "}
          <code className="font-mono">vis_nav_player</code> folder, then delete{" "}
          <code className="font-mono">data/maze.json</code>.
        </p>
      </div>
      <div className="bg-muted min-w-0 rounded-md">
        <pre className="overflow-x-auto p-3 text-xs leading-relaxed">
          <code>{content}</code>
        </pre>
        <div className="flex justify-end gap-1.5 border-t px-2 py-1.5">
          <Button size="xs" variant="outline" onClick={copy} className="bg-background">
            {copied ? (
              <Check className="size-3" aria-hidden />
            ) : (
              <Copy className="size-3" aria-hidden />
            )}
            {copied ? "Copied" : "Copy"}
          </Button>
          <Button
            size="xs"
            variant="outline"
            onClick={download}
            className="bg-background"
          >
            <Download className="size-3" aria-hidden />
            Download
          </Button>
        </div>
      </div>
    </div>
  );
}
